import { SearchSelectbox } from "cpl-react-common/components/Inputs/SearchSelectbox";
import { ErrorMessage, Field, Form, Formik } from "formik";
import * as Yup from "yup";
import { ActivityIndicator } from "cpl-react-common/components/ActivityIndicators/ActivityIndicator";
import SimpleModal from "cpl-react-common/components/Modals/SimpleModal";
import { useThunkDispatch } from "setup/redux/thunkDispatch";
import {
  selectLoadingPermissions,
  selectLoadingUsers,
  updateUserPermissionAssignment,
} from "./PermissionAssignmentsSlice";
import { useSelector } from "react-redux";

type NewPermissionValues = {
  userID: string | undefined;
  permissionID: number | undefined;
  granted: boolean;
};

const initialPermissionValues: NewPermissionValues = {
  userID: undefined,
  permissionID: undefined,
  granted: false,
};

const newPermissionValidationSchema = () =>
  Yup.object().shape(
    {
      userID: Yup.string().required("A valid user must be selected"),
      permissionID: Yup.number().required("A valid permission must be selected"),
    },
    []
  );

type NewPermissionModalProps = {
  showNewPermissionModal: boolean;
  setShowNewPermissionModal: React.Dispatch<React.SetStateAction<boolean>>;
  filteredUserList: {
    value: string;
    label: string;
  }[];
  permissionsList: {
    value: number;
    label: string;
  }[];
};

const NewPermissionModal = (props: NewPermissionModalProps) => {
  const thunkDispatch = useThunkDispatch();
  const loadingUsers = useSelector(selectLoadingUsers);
  const loadingPermissions = useSelector(selectLoadingPermissions);

  function addUserPermission({ userID, permissionID, granted }: NewPermissionValues) {
    if (userID != undefined && permissionID !== undefined) {
      thunkDispatch(updateUserPermissionAssignment({ userID, permissionID, grant: granted }));
      props.setShowNewPermissionModal(false);
    }
  }

  return (
    <SimpleModal
      modalTitle="New Permission"
      showModal={props.showNewPermissionModal}
      dismissModalCallback={() => props.setShowNewPermissionModal(false)}
    >
      <Formik
        initialValues={initialPermissionValues}
        validationSchema={newPermissionValidationSchema()}
        onSubmit={(values) => addUserPermission(values)}
        enableReinitialize={true}
      >
        {({ values, setFieldValue }) => {
          return (
            <Form>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label required fw-bold">User:</label>
                <div className="col-lg-9">
                  <ActivityIndicator isBusy={loadingUsers}>
                    <SearchSelectbox
                      data-test-id="new-permission-modal__user-select"
                      selectOptions={props.filteredUserList}
                      handleChange={(selection) => {
                        setFieldValue("userID", selection.value);
                      }}
                    />
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="userID" />
                      </div>
                    </div>
                  </ActivityIndicator>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label required fw-bold">Permission:</label>
                <div className="col-lg-9">
                  <ActivityIndicator isBusy={loadingPermissions}>
                    <SearchSelectbox
                      data-test-id="new-permission-modal__permission-select"
                      selectOptions={props.permissionsList}
                      handleChange={(selection) => {
                        setFieldValue("permissionID", selection.value);
                      }}
                    />
                    <div className="fv-plugins-message-container">
                      <div className="fv-help-block">
                        <ErrorMessage name="permissionID" />
                      </div>
                    </div>
                  </ActivityIndicator>
                </div>
              </div>
              <div className="row mb-6">
                <label className="col-lg-3 col-form-label fw-bold">Grant/Deny:</label>
                <div className="col-lg-9 d-flex align-items-center">
                  <Field
                    data-test-id="new-permission-modal__granted-checkbox"
                    name={"granted"}
                    className="d-flex form-control form-check-input"
                    type="checkbox"
                    checked={values.granted}
                  />
                </div>
              </div>
              <button
                data-test-id="new-permission-modal__add-button"
                type="submit"
                className="col-sm-3 btn btn-success btn-sm"
              >
                Add
              </button>
            </Form>
          );
        }}
      </Formik>
    </SimpleModal>
  );
};

export default NewPermissionModal;
